import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, DestroyRef, inject , signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { MapService } from 'src/app/services/map/map.service';
import { MobileResolutionService } from 'src/app/services/platform/mobile-resolution.service';
import { AccountService } from 'src/app/services/account/account.service';
import { VehicleHeaderComponent } from './vehicle-header/vehicle-header.component';
import { VehiclePropertyComponent, SliderProperty } from './vehicle-property/vehicle-property.component';
import { SettingsService } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { NavigationService } from 'src/app/services/core/navigation.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { AnalyticsLoggerService } from 'src/app/services/logging/analytics-logger.service';
import { ECarType } from 'src/app/models/ecar-type';
import { ECar, ECarSettings } from 'src/app/models/ecar';
import { CommonModule } from '@angular/common';
import { DisplayMode } from 'src/app/models/display-mode';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { VehicleOptionsDialogComponent } from '../../modals/vehicle-options-dialog/vehicle-options-dialog.component';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { WidgetService } from 'src/app/services/platform/widget.service';

@Component({
  standalone: true,
  selector: 'app-vehicle-panel',
  templateUrl: './vehicle-panel.component.html',
  styleUrls: ['./vehicle-panel.component.scss'],
  imports: [CommonModule, VehicleHeaderComponent, VehiclePropertyComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VehiclePanelComponent implements OnInit {
  @Input() navigation!: boolean;
  @Output() closePanel = new EventEmitter<void>();
  public readonly sliderProperties = signal<SliderProperty[]>([]);
  public readonly mobileResolution = signal<boolean>(false);
  public readonly mobileVisiblePanel = signal<string | null>(null);
  public readonly batteryLevel = signal<number>(73);
  public readonly batteryLevelRounded = signal<number>(73);
  public batteryLevelMin: number = 1;

  public selectedCar: ECarType = {
    Id: -1,
    CarWeight: null as unknown as number,
    ChargerTypes: null as unknown as number[],
    DesignCapacity: null as unknown as number,
    DragCoefficient: null as unknown as number,
    DragCross: null as unknown as number,
    Name: "",
    Type: null as unknown as string,
    Subtype: null as unknown as string,
    Range: null as unknown as number,
    FactoryRange: null as unknown as number,
    FactoryRangeSource: "",
    TopSpeed: null as unknown as number,
    TotalPower: null as unknown as number,
    Icon: null as unknown as string,
    FastChargePower: null as unknown as number,
    ChargePower: null as unknown as number,
    GreenCarsLink: null as unknown as string
  };
  public readonly selectedCarNameString = signal<string>("");
  public readonly selectedCarMultilineString = signal<string>("");

  private readonly destroyRef = inject(DestroyRef);
  private readonly widgetService = inject(WidgetService);
  public readonly manageVehicles = signal<boolean>(false);
  /** Widget `carselector=0` locks the vehicle: header not clickable, no Options button. */
  public readonly carSelectorEnabled = signal<boolean>(true);
  /** Widget `batteryslider=0` hides the battery slider. */
  public readonly batterySliderEnabled = signal<boolean>(true);
  public slidersValueChangedIn15seconds = false;

  updateCarSettignsInterval!: ReturnType<typeof setTimeout>;
  slidersValueChangedInterval!: ReturnType<typeof setTimeout>;
  changeUserSettingsInterval!: ReturnType<typeof setTimeout>;

  // display selected car, car properties sliders
  constructor(private waypointService: WaypointService, public mapService: MapService,
    public mobileResolutionService: MobileResolutionService,
    public accountService: AccountService,
    public settingsService: SettingsService,
    public languageService: LanguageService, private navigationService: NavigationService,
    private consoleLoggerService: ConsoleLoggerService, private loggerService: AnalyticsLoggerService,
    private stackedModalsService: StackedModalsService, private vehicleService: VehicleService) {
    this.selectedCar.Name = "";
    this.selectedCarNameString.set("");
    this.selectedCarMultilineString.set("");
    this.carSelectorEnabled.set(!this.widgetService.isWidget() || this.widgetService.carSelectorEnabled());
    this.batterySliderEnabled.set(!this.widgetService.isWidget() || this.widgetService.batterySliderEnabled());
  }

  ngOnInit() {
    // observe input params
    this.vehicleService.ObservableECarSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((settings) => {
      if (settings) {
        this.batteryLevel.set(settings.batteryLevel);
        this.batteryLevelRounded.set(Math.round(this.batteryLevel()));
        this.buildSliderProperties();
      }
    });

    // observe silent battery updates (e.g. predicted SOC during navigation), which
    // deliberately do not emit ObservableECarSettings so they cannot trigger a replan
    this.vehicleService.ObservableBatteryChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((battery) => {
      if (battery != null) {
        this.setbatteryLevel(battery);
      }
    });

    // observe selected car
    this.vehicleService.ObservableSelectedCar.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp?.ECarType) {
        this.selectedCar = resp.ECarType;
        this.selectedCarMultilineString.set("");
        if (resp.name.length > 0) {
          this.selectedCarNameString.set(resp.name);
        }
        else {
          this.selectedCarNameString.set(this.selectedCar.Name);
          if (this.selectedCarNameString() && this.selectedCarNameString().toLocaleLowerCase().includes("tesla model")) {
            this.selectedCarMultilineString.set(this.selectedCarNameString().substr(14));
            this.selectedCarNameString.set(this.selectedCarNameString().substr(0, 13));
          }
        }
      }
    });

    //observe selected mode
    this.waypointService.ObservableSelectedDisplayMode.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        if (resp != DisplayMode.SHOW_ON_MAP) {
          this.vehicleService.setSliderRangeInputParams(this.getRangeSliderParams());
        }
        this.buildSliderProperties();
      }
    });

    this.languageService.activeLanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.buildSliderProperties();
      }
    });

    this.loadSettings();

    this.buildSliderProperties();

    this.accountService.ObservableIsAuthorized.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != undefined) {
        this.manageVehicles.set(resp);
        if (resp == false) {
          this.loadSettings();
        }
      }
    });

    // observe mobile resolution
    this.mobileResolutionService.ObservableMobileResolution.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != null) {
        this.mobileResolution.set(resp);
      }
    });

    this.mobileResolutionService.ObservableMobileVisiblePanel.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.mobileVisiblePanel.set(resp);
      }
    });

    this.navigationService.ObservableNextTurn.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp && this.navigation && !this.slidersValueChangedIn15seconds) {
        this.batteryLevel.set(resp.EstimatedBatteryLevel);
        this.batteryLevelRounded.set(Math.round(this.batteryLevel()));
        this.buildSliderProperties();
      }
    });
  }

  private loadSettings(): void {
    if (this.settingsService.acceptCookies && !this.accountService.getIsAuthorized()) {
      // Widget `battery` beats the stored level; the other URL car params are re-applied
      // below, after loadCarSettingsCookies() has restored SOH / fast charge limit.
      const widgetBattery = this.widgetService.isWidget() ? this.widgetService.getCarParams().settings.batteryLevel : undefined;
      const batteryLevel = widgetBattery != null ? widgetBattery.toString() : localStorage.getItem('batteryLevel');
      if (batteryLevel) {
        this.batteryLevel.set(parseFloat(batteryLevel));
        this.batteryLevelRounded.set(Math.round(this.batteryLevel()));
      }
      this.sliderParamsChanged();
      this.vehicleService.loadCarSettingsCookies();
      this.vehicleService.applyWidgetCarSettings();
    }
    if (!this.settingsService.acceptCookies) {
      this.setDefaultSettings();
    }
  }

  private setDefaultSettings(): void {
    this.batteryLevel.set(73);
    this.batteryLevelRounded.set(73);
    this.sliderParamsChanged();
  }

  public openCarSelectorDialog(): void {
    this.stackedModalsService.openModal(VehicleOptionsDialogComponent,
      { data: {}, autoFocus: false, panelClass: 'dialog-snap-to-fullscreen' });
  }

  public setbatteryLevel(batteryLevel: number): void {
    this.batteryLevel.set(batteryLevel);
    this.batteryLevelRounded.set(Math.round(this.batteryLevel()));
    this.buildSliderProperties();
  }

  // range slider change
  public sliderParamsChanged(): void {
    if (this.navigation) {
      this.slidersValueChangedIn15seconds = true;
      this.navigationService.setSlidersValueChangedIn15seconds(true);
      clearTimeout(this.slidersValueChangedInterval);
      this.slidersValueChangedInterval = setTimeout(() => {
        this.slidersValueChangedIn15seconds = false;
        this.navigationService.setSlidersValueChangedIn15seconds(false);
      }, 15000);
    }

    this.vehicleService.setSliderRangeInputParams(this.getRangeSliderParams());
    if (this.settingsService.acceptCookies && !this.accountService.getIsAuthorized()) {
      localStorage.setItem('batteryLevel', this.batteryLevel().toString());
    }

    if (this.accountService.getIsAuthorized()) {
      clearTimeout(this.updateCarSettignsInterval);
      const selectedCarArray = this.accountService.user.Ecars.filter((ecar: ECar) => ecar.selected === true);

      this.updateCarSettignsInterval = setTimeout(() => {
        if (selectedCarArray.length > 0) {
          const settings = this.getRangeSliderParams();
          const rangeInputParams = this.vehicleService.getECarSettings();
          if (rangeInputParams && rangeInputParams.batteryStateOfHealth) {
            settings.batteryStateOfHealth = rangeInputParams.batteryStateOfHealth;
          }
          if (rangeInputParams && rangeInputParams.fastChargeLimit) {
            settings.fastChargeLimit = rangeInputParams.fastChargeLimit;
          }
          const userCarSettings: any = this.accountService.user.getSelectedUserCar().settings;
          settings.tripRecording = userCarSettings.tripRecording != undefined ? userCarSettings.tripRecording : 1;
          this.accountService.editVehicle(selectedCarArray[0].userVehicleID, null, null, null, JSON.stringify(settings), false).subscribe();
        }
      }, 1000);
    }
  }

  public batterySliderChanged(): void {
    this.loggerService.onBatteryChangeEvent(this.batteryLevel());
  }

  // getting object that contains sliders current values (only batteryLevel is owned here;
  // other slider values are managed by the vehicle-options-dialog and read back from the service)
  public getRangeSliderParams(): ECarSettings {
    const current = this.vehicleService.getECarSettings();
    return {
      batteryLevel: this.batteryLevel(),
      weight: current?.weight ?? 2,
      tirePressure: current?.tirePressure ?? 2.3,
      userBehavior: current?.userBehavior ?? 50,
      speedLimiter: current?.speedLimiter ?? 100,
      batterySafetyLimitRha: current?.batterySafetyLimitRha ?? 50,
      batterySafetyLimitRoute: current?.batterySafetyLimitRoute ?? 15,
      minBatteryAtDestination: current?.minBatteryAtDestination ?? 10
    };
  }

  public buildSliderProperties(): void {
    const lang = this.languageService.languageJSON;
    this.sliderProperties.set([
      {
        id: 'batteryLevel',
        label: lang?.SliderSidebar_BatteryLevel || '',
        displayValue: this.batteryLevelRounded() + ' %',
        min: this.batteryLevelMin,
        max: 100,
        digit: 0,
        step: 90,
        altString: 'battery level',
        value: this.batteryLevel(),
        visible: this.batterySliderEnabled()
      }
    ]);
  }

  public onSliderDrag(id: string, value: number): void {
    if (id === 'batteryLevel') {
      this.batteryLevel.set(value);
      this.batteryLevelRounded.set(Math.round(value));
    }
    this.buildSliderProperties();
  }

  public onSliderChange(id: string, value: number): void {
    this.onSliderDrag(id, value);
    if (id === 'batteryLevel') {
      this.batterySliderChanged();
    }
    this.sliderParamsChanged();
  }

  public onCloseClick(): void {
    this.closePanel.emit();
  }

}
